import { useMemo, useState } from "react";

/**
 * Orders rows by one key. Numbers compare numerically, anything else compares
 * as text, and `tieKey` keeps equal values in a stable, readable order.
 *
 * Kept separate from the hook so it can be exercised on its own.
 */
export const sortRows = ({ rows, orderBy, order, getValue, tieKey }) => {
  const direction = order === "asc" ? 1 : -1;

  return [...rows].sort((a, b) => {
    const left = getValue(a, orderBy);
    const right = getValue(b, orderBy);

    let result;
    if (typeof left === "number" && typeof right === "number") {
      result = left - right;
    } else {
      result = String(left ?? "").localeCompare(String(right ?? ""));
    }

    if (result !== 0) return result * direction;
    return String(tieKey(a)).localeCompare(String(tieKey(b)));
  });
};

/**
 * Client-side sorting for the report tables.
 *
 * These tables arrive whole (they are not server-paginated), so sorting them
 * here avoids a round trip. A numeric column opens on descending because
 * "which is the biggest" is the question being asked; a text column opens on
 * ascending.
 */
export const useTableSort = ({
  rows = [],
  getValue,
  isNumeric,
  tieKey,
  defaultOrderBy = "total",
  defaultOrder = "desc",
}) => {
  const [orderBy, setOrderBy] = useState(defaultOrderBy);
  const [order, setOrder] = useState(defaultOrder);

  const onSort = (key) => {
    if (key === orderBy) {
      setOrder(order === "asc" ? "desc" : "asc");
      return;
    }
    setOrderBy(key);
    setOrder(isNumeric(key) ? "desc" : "asc");
  };

  const sorted = useMemo(
    () => sortRows({ rows, orderBy, order, getValue, tieKey }),
    [rows, orderBy, order, getValue, tieKey]
  );

  return { order, orderBy, onSort, sorted };
};

export default useTableSort;
